import { Preferences } from '@capacitor/preferences';
import type { PushHubDestination } from '@apexx/shell-contract';

/** Preferences key for a pending FCM tap destination (survives cold start + login). */
export const PENDING_PUSH_PREFS_KEY = 'apexx.pendingPushNavigation';

/** Discard pending push navigation older than this. */
export const PENDING_PUSH_TTL_MS = 24 * 60 * 60 * 1000;

export type PendingPushNavigation = PushHubDestination & {
	type: string;
	savedAt: number;
};

function isPendingPushExpired(pending: PendingPushNavigation): boolean {
	return Date.now() - pending.savedAt > PENDING_PUSH_TTL_MS;
}

function parsePendingPushJson(raw: string): PendingPushNavigation | null {
	try {
		const parsed = JSON.parse(raw) as Partial<PendingPushNavigation>;
		if (
			typeof parsed.hubPath !== 'string' ||
			typeof parsed.appId !== 'string' ||
			typeof parsed.subPath !== 'string' ||
			typeof parsed.requiredRole !== 'string' ||
			typeof parsed.type !== 'string' ||
			typeof parsed.savedAt !== 'number'
		) {
			return null;
		}

		return {
			hubPath: parsed.hubPath,
			appId: parsed.appId,
			subPath: parsed.subPath,
			requiredRole: parsed.requiredRole,
			type: parsed.type,
			savedAt: parsed.savedAt,
		};
	} catch {
		return null;
	}
}

export async function savePendingPush(
	destination: PushHubDestination,
	type: string,
): Promise<void> {
	const pending: PendingPushNavigation = {
		...destination,
		type,
		savedAt: Date.now(),
	};

	await Preferences.set({
		key: PENDING_PUSH_PREFS_KEY,
		value: JSON.stringify(pending),
	});
}

export async function peekPendingPush(): Promise<PendingPushNavigation | null> {
	const { value } = await Preferences.get({ key: PENDING_PUSH_PREFS_KEY });
	if (!value) {
		return null;
	}

	const pending = parsePendingPushJson(value);
	if (!pending || isPendingPushExpired(pending)) {
		await clearPendingPush();
		return null;
	}

	return pending;
}

export async function hasPendingPush(): Promise<boolean> {
	return (await peekPendingPush()) !== null;
}

/** Read and clear the pending push destination. */
export async function takePendingPush(): Promise<PendingPushNavigation | null> {
	const pending = await peekPendingPush();
	if (!pending) {
		return null;
	}

	await clearPendingPush();
	return pending;
}

export async function clearPendingPush(): Promise<void> {
	await Preferences.remove({ key: PENDING_PUSH_PREFS_KEY });
}
